import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  GetClientRecordsListDocument,
  GetEngagementRecordsListDocument,
  type GetClientRecordsListQuery,
  type GetClientRecordsListQueryVariables,
  type GetEngagementRecordsListQuery,
  type GetEngagementRecordsListQueryVariables,
} from '@/gql/operations';

import useBuildGraphQLVariables, { applyClientSideFilters } from '../hooks/useBuildGraphQLVariables';
import { parseEngagementListRecordsAsync } from '../hooks/useParseRecords';

import {
  currentPageAtom,
  engagementRecordsAtom,
  isLoadingRecordsAtom,
  loadedReloadTokenAtom,
  pageSizeAtom,
  recordsErrorAtom,
  reloadRecordsAtom,
} from './atoms';

type EngagementRow = GetEngagementRecordsListQuery['clm_engagement_records'][number];

// Typed documents so client.query infers result/variable types without deprecated generics.
const CLIENT_RECORDS_QUERY = GetClientRecordsListDocument as TypedDocumentNode<
  GetClientRecordsListQuery,
  GetClientRecordsListQueryVariables
>;
const ENGAGEMENT_RECORDS_QUERY = GetEngagementRecordsListDocument as TypedDocumentNode<
  GetEngagementRecordsListQuery,
  GetEngagementRecordsListQueryVariables
>;

// Fetch the full dataset in pages instead of a single request. Loading the whole
// ~140k-row dataset at once times out on the server, so we page through it in
// fixed-size batches and concatenate the results.
//
// Keyset pages are cheap (a 10k-row page is only ~400kB and returns fast), and
// the pages within a dataset are fetched serially because each one needs the
// previous page's cursor. A larger page size therefore cuts the number of serial
// round trips roughly in half while staying comfortably under the server timeout.
const PAGE_SIZE = 20000;

/**
 * Repeatedly queries `document` in fixed-size batches until a page returns fewer
 * rows than requested, then returns all rows concatenated.
 *
 * Uses *keyset* (cursor) pagination rather than OFFSET: each page filters on the
 * last-seen ordering key (`buildVariables` receives the last row of the previous
 * page). OFFSET pagination degrades badly at depth because the database must scan
 * and discard every skipped row, so each successive page gets slower. Keyset
 * pagination seeks straight to the next key via the ordering index, keeping every
 * page equally fast.
 *
 * `buildVariables` receives `undefined` for the first page (no cursor yet) and the
 * last row of the previous page thereafter. `selectRows` extracts the row array
 * from each query result.
 */
async function fetchAllPages<TData, TVars extends OperationVariables, TRow>(
  client: ReturnType<typeof useApolloClient>,
  document: TypedDocumentNode<TData, TVars>,
  selectRows: (data: TData | undefined) => TRow[],
  buildVariables: (lastRow: TRow | undefined) => TVars,
  isCancelled: () => boolean,
): Promise<TRow[]> {
  const allRows: TRow[] = [];
  let lastRow: TRow | undefined;

  for (;;) {
    if (isCancelled()) break;

    const { data } = await client.query({
      query: document,
      variables: buildVariables(lastRow),
      fetchPolicy: 'no-cache',
    });

    const rows = selectRows(data);
    allRows.push(...rows);

    // Last page reached when the server returns fewer rows than we asked for.
    if (rows.length < PAGE_SIZE) break;

    lastRow = rows.at(-1);
  }

  return allRows;
}

/**
 * Loader hook — fetches the entire engagement dataset and stores the parsed
 * records in shared Jotai atoms. Should be mounted once (at the Dashboard).
 *
 * The client list and the engagement records are fetched as two separate flat
 * queries (no nested joins) and merged by client number, which is dramatically
 * faster than a single deeply-nested query for the full ~140k-row dataset.
 */
export const useEngagementRecords = () => {
  const client = useApolloClient();
  const records = useAtomValue(engagementRecordsAtom);
  const setRecords = useSetAtom(engagementRecordsAtom);
  const setLoading = useSetAtom(isLoadingRecordsAtom);
  const setError = useSetAtom(recordsErrorAtom);
  const reloadToken = useAtomValue(reloadRecordsAtom);
  const [loadedToken, setLoadedToken] = useAtom(loadedReloadTokenAtom);

  useEffect(() => {
    let cancelled = false;

    // Data lives in shared atoms that persist across navigation, so re-mounting the
    // dashboard (e.g. clicking back from the detail page) should not re-fetch the
    // full ~140k-row dataset. Only load on the first mount or when a reload was
    // explicitly requested (reloadToken changed).
    const alreadyLoaded = loadedToken === reloadToken && records.length > 0;

    const loadAll = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fire both paged requests at once so they run concurrently instead of
        // waiting for the (large) client query to finish before starting the
        // engagement query. They hit different tables and are independent.
        //
        // Each dataset is fetched in fixed-size pages (see fetchAllPages) because
        // loading the full ~140k-row dataset in a single request times out on the
        // server.
        //
        // fetchPolicy 'no-cache' skips Apollo's normalized-cache write, which for
        // this ~140k-row dataset (with nested attorney/practice-group objects)
        // is a very expensive synchronous main-thread operation. The data lives
        // in shared Jotai atoms, so the Apollo cache isn't needed here.
        const [clientRows, engagementRows] = await Promise.all([
          fetchAllPages(
            client,
            CLIENT_RECORDS_QUERY,
            (data) => data?.mlfsl_client ?? [],
            // Keyset cursor: page on ClientNumber (the ordering key) so each page
            // seeks via the index instead of scanning past a growing offset.
            (lastRow): GetClientRecordsListQueryVariables => ({
              limit: PAGE_SIZE,
              where: lastRow ? { ClientNumber: { _gt: lastRow.ClientNumber } } : undefined,
            }),
            () => cancelled,
          ),
          fetchAllPages(
            client,
            ENGAGEMENT_RECORDS_QUERY,
            (data) => data?.clm_engagement_records ?? [],
            // Keyset cursor: page on id (the ordering key).
            (lastRow): GetEngagementRecordsListQueryVariables => ({
              limit: PAGE_SIZE,
              where: lastRow ? { id: { _gt: lastRow.id } } : undefined,
            }),
            () => cancelled,
          ),
        ]);

        // Index engagement records by client number for the merge.
        const engagementByClient = new Map<string, EngagementRow>();
        for (const record of engagementRows) {
          engagementByClient.set(record.client_number, record);
        }

        // Parse in chunks that yield to the event loop so processing the full
        // ~140k-row dataset doesn't freeze the page.
        const parsed = await parseEngagementListRecordsAsync(clientRows, engagementByClient, {
          isCancelled: () => cancelled,
        });

        if (!cancelled) {
          setRecords(parsed);
          setLoadedToken(reloadToken);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load records'));
          setLoading(false);
        }
      }
    };

    if (!alreadyLoaded) {
      void loadAll();
    }

    return () => {
      cancelled = true;
    };
  }, [client, reloadToken, loadedToken, records.length, setRecords, setLoading, setError, setLoadedToken]);
};

/**
 * Reader hook — reads the loaded engagement records from shared state and applies
 * all filters client-side.
 */
export const useEngagementRecordsValue = () => {
  const records = useAtomValue(engagementRecordsAtom);
  const isLoading = useAtomValue(isLoadingRecordsAtom);
  const error = useAtomValue(recordsErrorAtom);
  const setReloadToken = useSetAtom(reloadRecordsAtom);
  const setPage = useSetAtom(currentPageAtom);

  const { clientSideFilters } = useBuildGraphQLVariables();

  const filteredRecords = useMemo(
    () => applyClientSideFilters(records, clientSideFilters),
    [records, clientSideFilters],
  );

  // Reset to the first page whenever the active filters change so the client-side
  // paginated grid never lands on an out-of-range page.
  const filtersKey = JSON.stringify(clientSideFilters);
  const prevFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (prevFiltersKey.current !== filtersKey) {
      prevFiltersKey.current = filtersKey;
      setPage(0);
    }
  }, [filtersKey, setPage]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), [setReloadToken]);

  return {
    engagementRecords: filteredRecords,
    totalCount: filteredRecords.length,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for pagination controls
 */
export const usePagination = () => {
  const [page, setPage] = useAtom(currentPageAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
  };
};
